// Stable className strings backed by globally-injected CSS. Preserves the
// `useStyles().<key>` consumer API without @mui/styles.
import { useTheme, alpha } from '@mui/material/styles';

export const dims = {
  img: { width: 250, height: 250 },
};

const BUFF = 64;
const PREFIX = 'globalImageGrid';

const classes = {
  buttonRow: `${PREFIX}-buttonRow`,
  button: `${PREFIX}-button`,
  splitButtonGroup: `${PREFIX}-splitButtonGroup`,
  splitButtonPrimary: `${PREFIX}-splitButtonPrimary`,
  splitButtonToggle: `${PREFIX}-splitButtonToggle`,
  popper: `${PREFIX}-popper`,
  row: `${PREFIX}-row`,
  selectedRow: `${PREFIX}-selectedRow`,
  selectRowFixed: `${PREFIX}-selectRowFixed`,
  selButtonRow: `${PREFIX}-selButtonRow`,
  selButton: `${PREFIX}-selButton`,
  selDelete: `${PREFIX}-selDelete`,
  vr: `${PREFIX}-vr`,
  searchContainer: `${PREFIX}-searchContainer`,
  gridList: `${PREFIX}-gridList`,
  titleBar: `${PREFIX}-titleBar`,
  gridTile: `${PREFIX}-gridTile`,
  gridTileSelected: `${PREFIX}-gridTileSelected`,
  gridListFolder: `${PREFIX}-gridListFolder`,
  divider: `${PREFIX}-divider`,
  hrDivider: `${PREFIX}-hrDivider`,
  popOver: `${PREFIX}-popOver`,
  icon: `${PREFIX}-icon`,
  imgContainer: `${PREFIX}-imgContainer`,
  img: `${PREFIX}-img`,
  foldersRow: `${PREFIX}-foldersRow`,
  chosenFolder: `${PREFIX}-chosenFolder`,
  cfTitle: `${PREFIX}-cfTitle`,
  cfSize: `${PREFIX}-cfSize`,
  closeFolder: `${PREFIX}-closeFolder`,
  filterRow: `${PREFIX}-filterRow`,
  pos: `${PREFIX}-pos`,
  neg: `${PREFIX}-neg`,
  iconRoot: `${PREFIX}-iconRoot`,
  checkIcon: `${PREFIX}-checkIcon`,
  checkIconSelectMode: `${PREFIX}-checkIconSelectMode`,
  checkIconSelected: `${PREFIX}-checkIconSelected`,
  photoText: `${PREFIX}-photoText`,
  photoTextSelected: `${PREFIX}-photoTextSelected`,
  toolBtn: `${PREFIX}-toolBtn`,
  tool0: `${PREFIX}-tool0`,
  tool1: `${PREFIX}-tool1`,
  tool2: `${PREFIX}-tool2`,
  tool3: `${PREFIX}-tool3`,
};

let injectedHash = null;
const themeHash = (theme) => `${theme.palette.secondary.main}`;
const injectStyles = (theme) => {
  if (typeof document === 'undefined') return;
  const hash = themeHash(theme);
  if (injectedHash === hash) return;
  const prev = document.getElementById(`${PREFIX}-styles`);
  if (prev) prev.remove();
  injectedHash = hash;

  const css = `
    .popContainer { padding: ${theme.spacing(1)}; }
    .popContainer .spaceVert { margin: ${theme.spacing(3)} 0; }

    .${PREFIX}-buttonRow {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      margin: ${theme.spacing(3)} 0;
      align-items: center;
      gap: ${theme.spacing(1.5)};
      max-width: 100%;
    }
    .${PREFIX}-button { flex: 0 1 auto; min-width: fit-content; }
    .${PREFIX}-splitButtonGroup { flex: 0 1 auto; max-width: 100%; }
    .${PREFIX}-splitButtonPrimary { flex: 1 1 auto; min-width: 0; margin-right: 0 !important; }
    .${PREFIX}-splitButtonToggle { flex: 0 0 36px; min-width: 36px; margin-right: 0 !important; }
    .${PREFIX}-popper { z-index: 1; }
    .${PREFIX}-row { flex-direction: row; display: flex; align-items: center; flex-wrap: wrap; }
    .${PREFIX}-selectedRow {
      position: absolute;
      left: 0;
      right: 0;
      background-color: ${alpha(theme.palette.primary.main, 0.25)};
      align-items: center;
      padding: ${theme.spacing(1)} ${theme.spacing(2)};
    }
    .${PREFIX}-selectedRow button {
      min-width: auto;
      margin-right: ${theme.spacing(0.5)};
      margin-bottom: ${theme.spacing(0.5)};
    }
    .${PREFIX}-selectedRow button .MuiIcon-root { font-size: 15px; }
    .${PREFIX}-selectedRow button svg { color: ${theme.palette.common.white}; }
    .${PREFIX}-selectRowFixed { position: fixed; top: 0; left: 0; right: 0; z-index: 100; }
    .${PREFIX}-selButtonRow { margin-left: auto; display: flex; flex-direction: row; flex-wrap: wrap; }
    .${PREFIX}-selButton {}
    .${PREFIX}-selDelete { background-color: ${theme.palette.error.main}; color: ${theme.palette.common.white}; }
    .${PREFIX}-vr {
      height: 33px;
      width: 1px;
      margin: 0 ${theme.spacing(1)} 0 ${theme.spacing(0.5)};
      background-color: ${theme.palette.common.black};
    }
    .${PREFIX}-searchContainer { margin: ${theme.spacing(3)} -7px; }
    .${PREFIX}-gridList { justify-content: left; }
    .${PREFIX}-gridList .MuiImageListItem-root { cursor: pointer; }
    .${PREFIX}-titleBar {
      height: ${theme.spacing(6)};
      background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%);
    }
    .${PREFIX}-gridTile { position: relative; }
    /* Thumb conversions are square. Until those exist the grid falls back to the
       original file. Keep the tile square and cover it so the longer sides clip
       instead of leaving a gap. */
    .${PREFIX}-gridTile.MuiImageListItem-root {
      border-radius: 4px;
      overflow: hidden;
      aspect-ratio: 1;
    }
    .${PREFIX}-gridTile.MuiImageListItem-root > div:first-child {
      position: absolute;
      inset: 0;
    }
    .${PREFIX}-gridTile.MuiImageListItem-root > div:first-child > div:first-child,
    .${PREFIX}-gridTile.MuiImageListItem-root .smallMediaContainer,
    .${PREFIX}-gridTile.MuiImageListItem-root .smallMediaContainer > div {
      width: 100%;
      height: 100%;
    }
    .${PREFIX}-gridTile.MuiImageListItem-root .smallMediaContainer img {
      object-fit: cover;
    }
    .${PREFIX}-gridTile.MuiImageListItem-root::before {
      pointer-events: none;
      content: "";
      display: block;
      position: absolute;
      z-index: 10;
      height: 0%;
      width: 100%;
      top: 0;
      transition: height 0.25s ease-out;
      background: linear-gradient(to top, transparent 0%, #000000aa 100%);
    }
    .${PREFIX}-gridTile.MuiImageListItem-root:hover:before { height: 40%; }
    .${PREFIX}-gridTile:hover button { opacity: 1; }
    .${PREFIX}-gridTile:hover div #photoText { background-color: rgba(0,0,0,0.4); }
    .${PREFIX}-gridTileSelected { background-color: ${theme.palette.secondary.main}33; }
    .${PREFIX}-gridTileSelected .smallMediaContainer {
      margin: ${theme.spacing(1.5)};
      width: calc(100% - ${theme.spacing(3)}) !important;
      height: calc(100% - ${theme.spacing(3)}) !important;
    }
    .${PREFIX}-gridListFolder { max-width: max(25%, 216px); position: relative; }
    .${PREFIX}-gridListFolder .MuiImageListItemBar-root { display: flex; height: ${theme.spacing(8)}; }
    .${PREFIX}-divider { clear: both; }
    .${PREFIX}-hrDivider {
      clear: both;
      width: 100% !important;
      height: 31px !important;
      border-bottom: 4px solid ${theme.palette.action.disabled};
      font-size: 14px;
      box-sizing: border-box;
      cursor: default !important;
    }
    .${PREFIX}-popOver {}
    .${PREFIX}-icon { color: white; }
    .${PREFIX}-imgContainer {
      background-color: transparent;
      width: 100%;
      height: 100%;
      object-fit: contain;
      color: ${theme.palette.text.secondary};
    }
    .${PREFIX}-imgContainer .mui-image-wrapper img { width: 100% !important; height: 100% !important; }
    .${PREFIX}-img { width: ${dims.img.width}px; height: ${dims.img.height}px; }
    .${PREFIX}-foldersRow {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      width: 100%;
      margin: ${theme.spacing(2)} 0;
      align-items: center;
    }
    .${PREFIX}-chosenFolder {}
    .${PREFIX}-cfTitle { padding-right: 50px; text-align: left; margin: 0; margin-bottom: 5px; }
    .${PREFIX}-cfSize { text-align: left; margin: 0; font-size: 10px; }
    .${PREFIX}-closeFolder { position: absolute; right: 0; top: 0; color: ${theme.palette.secondary.contrastText}; }
    .${PREFIX}-filterRow {
      display: flex;
      flex-direction: row;
      max-height: 30px;
      margin-left: ${theme.spacing(1)};
    }
    .${PREFIX}-filterRow .MuiInputBase-root { margin-right: ${theme.spacing(2)}; }
    .${PREFIX}-filterRow button {
      min-width: auto;
      margin-right: ${theme.spacing(0.5)};
      margin-top: ${theme.spacing(0.5)};
    }
    .${PREFIX}-filterRow button .MuiIcon-root { font-size: 15px; }
    .${PREFIX}-pos { color: ${theme.palette.success.main}; }
    .${PREFIX}-neg { color: ${theme.palette.error.main}; }
    .${PREFIX}-iconRoot:hover { background-color: rgb(255,255,255,0.08); }
    .${PREFIX}-checkIcon {
      position: absolute;
      top: 4px;
      left: 4px;
      z-index: 20;
      opacity: 0.4;
      color: white;
    }
    .${PREFIX}-checkIconSelectMode { opacity: 1; }
    .${PREFIX}-checkIconSelected {}
    .${PREFIX}-photoText {
      display: flex;
      flex-direction: row;
      align-items: center;
      position: absolute;
      bottom: 0;
      height: 40px;
      background-color: rgba(0,0,0,0.3);
      width: 100%;
    }
    .${PREFIX}-photoText .icon {
      width: 40px;
      color: ${theme.palette.common.white};
      align-items: center;
      justify-content: center;
      display: flex;
    }
    .${PREFIX}-photoText .icon .MuiIcon-root { font-size: 15px; }
    .${PREFIX}-photoText .icon .pos { color: ${theme.palette.success.main}; }
    .${PREFIX}-photoText .icon .neg { color: ${theme.palette.error.main}; }
    .${PREFIX}-photoText .textContainer {
      color: ${theme.palette.common.white};
      width: calc(100% - 40px);
      justify-content: center;
      display: flex;
      flex-direction: column;
    }
    .${PREFIX}-photoText .textContainer .title {
      display: inline-block;
      text-overflow: ellipsis;
      position: relative;
      overflow: hidden;
      white-space: nowrap;
      width: 95%;
    }
    .${PREFIX}-photoText .textContainer .subtitle {
      font-size: 12px;
      display: inline-block;
      text-overflow: ellipsis;
      position: relative;
      overflow: hidden;
      white-space: nowrap;
      width: 95%;
    }
    .${PREFIX}-photoTextSelected {
      bottom: ${theme.spacing(1.5)};
      width: calc(100% - ${theme.spacing(3)});
      left: ${theme.spacing(1.5)};
    }
    .${PREFIX}-toolBtn { position: absolute; left: 0; top: 0; }
    .${PREFIX}-toolBtn svg { color: ${theme.palette.common.white}; }
    .${PREFIX}-tool0 {}
    .${PREFIX}-tool1 { left: ${BUFF * 1}px; }
    .${PREFIX}-tool2 { left: ${BUFF * 2}px; }
    .${PREFIX}-tool3 { left: ${BUFF * 3}px; }

    /* PFM-2630: on mobile the surrounding PhotosGrid provides one consistent
       horizontal margin (gutter), so the inner rows must align flush to that
       container edge. Zero out the per-element offsets that otherwise break
       alignment: the search container's negative side margin and the "By
       Location" select's left margin (from the shared global select class).
       Desktop is unchanged. */
    ${theme.breakpoints.down('md')} {
      .${PREFIX}-searchContainer { margin-left: 0; margin-right: 0; }
      .${PREFIX}-foldersRow > .MuiInputBase-root { margin-left: 0; }
      /* SearchBar wraps its content in Box > Container, and that Container has a
         spacing(1) all-around margin which pushes the search box 8px in from the
         gutter edge (out of line with the Upload/By-Location rows). Zero its
         horizontal margin (scoped + specific enough to win over the emotion
         style) so the search box edge aligns with the other rows. */
      .${PREFIX}-searchContainer > .MuiBox-root > div { margin-left: 0; margin-right: 0; }
    }
  `;

  const tag = document.createElement('style');
  tag.id = `${PREFIX}-styles`;
  tag.textContent = css;
  document.head.appendChild(tag);
};

export const useStyles = () => {
  const theme = useTheme();
  injectStyles(theme);
  return classes;
};

export default useStyles;
